import { useRef, useState } from 'react';
import { Upload, Loader2, X, ExternalLink } from 'lucide-react';
import { uploadFile } from '@/lib/upload';

export interface UploadMeta {
  nome: string;
  tipo: string;
}

interface FileUploadProps {
  label?: string;
  error?: string;
  accept?: string;
  required?: boolean;
  value?: string;
  onUploadComplete: (url: string, meta?: UploadMeta) => void;
  onClear: () => void;
}

export function FileUpload({
  label,
  error,
  accept = 'image/jpeg,image/png,image/webp,image/gif,application/pdf',
  required,
  value,
  onUploadComplete,
  onClear,
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleClick = () => {
    if (!isUploading) {
      inputRef.current?.click();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      const result = await uploadFile(file);
      setFileName(result.nome);
      onUploadComplete(result.url, { nome: result.nome, tipo: result.tipo });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Erro ao enviar arquivo');
    } finally {
      setIsUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFileName(null);
    setUploadError(null);
    onClear();
  };

  const displayError = error || uploadError;

  return (
    <div className="w-full">
      {label && (
        <label className="mb-1 block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className="hidden"
      />

      {value ? (
        <div className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2">
          <span className="flex-1 truncate text-sm text-gray-700">
            {fileName || 'Arquivo enviado'}
          </span>
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:text-primary-700"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-4 w-4" />
          </a>
          <button
            type="button"
            onClick={handleClear}
            className="text-gray-400 hover:text-red-500"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleClick}
          disabled={isUploading}
          className={`
            flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed px-3 py-3
            text-sm transition-colors
            ${isUploading
              ? 'cursor-wait border-gray-300 bg-gray-50 text-gray-400'
              : 'cursor-pointer border-gray-300 text-gray-500 hover:border-primary-400 hover:text-primary-600'
            }
            ${displayError ? 'border-red-300' : ''}
          `}
        >
          {isUploading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Enviando...
            </>
          ) : (
            <>
              <Upload className="h-4 w-4" />
              Clique para selecionar
            </>
          )}
        </button>
      )}

      {displayError && <p className="mt-1 text-sm text-red-600">{displayError}</p>}
    </div>
  );
}
